import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { APP_CONFIG } from "../../../constants/constants";
import ReportTypeSelector from "./reportTypeSelector";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import svgIcons from "../../../assets/svg/svgIcons";
import { TReport } from "../../../api/types/Report";
import { routeUrls } from "../../../navigation/routeUrls";

interface HeaderBarProps {
  preTitle?: string;
  title: string;
  subTitle?: string | React.ReactNode;
  reportOnDisplay?: TReport;
  showBackButton?: boolean;
  backButtonCallback?: () => void;
  rightComponent?: React.ReactNode;
  showRefreshButton?: boolean;
  wrapperClassName?: string;
}

const HeaderBar: FC<HeaderBarProps> = ({
  preTitle,
  title,
  subTitle,
  reportOnDisplay,
  showBackButton = false,
  backButtonCallback,
  rightComponent = null,
  showRefreshButton = false, // enable when functionality gets implemented, also needs a callback
  wrapperClassName = `flex justify-between ${APP_CONFIG.DES.DASH.P_HORIZ} py-2`,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "admins" });
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const handleShareButtonClick = () => {
    if (!reportOnDisplay) return;
    const newUrl =
      routeUrls.dashboardChildren.reportsChildren.reportPrefix +
      routeUrls.dashboardChildren.reportsChildren.reportSharingSuffixUri
        .replace(":reportType", `${reportOnDisplay.type}`)
        .replace(":categoryId", `${reportOnDisplay.category}`)
        .replace(":reportId", `${reportOnDisplay.id}`);
    navigate(newUrl);
  };

  const handleInternalShareButtonClick = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        showInternalShareReportModal: true,
      })
    );
  };

  return (
    <header className={wrapperClassName}>
      <div className="flex items-center">
        {showBackButton && (
          <img
            src={svgIcons.LeftArrowIcon}
            alt="left-arrow-icon"
            className="mr-4 p-2 bg-accent-blue-pale rounded-full cursor-pointer"
            onClick={() => {
              if (!!backButtonCallback) backButtonCallback();
              else navigate(-1);
            }}
          />
        )}
        <button
          className={`flex flex-col hover:bg-accent-blue-pale${modalsState.showReportTypeSelector ? " outline" : ""} focus:outline hover:outline outline-4 outline-accent-blue-paleO66 rounded-lg ps-2 -translate-x-2`}
          type="button"
          onClick={() => {
            dispatch(
              setModalsData({
                ...modalsState,
                showReportTypeSelector: !modalsState.showReportTypeSelector,
              })
            );
          }}
        >
          {preTitle && (
            <h4 className="text-sm font-medium text-heading-gray444 text-left">
              {preTitle}
            </h4>
          )}
          <div className="flex flex-col items-start">
            <h1 className="font-bold text-sidemenu-text-active text-xl leading-6 text-left">
              <span className="flex gap-2">
                <span>{title}</span>
                <img
                  alt="download"
                  src={
                    modalsState.showReportTypeSelector
                      ? svgIcons.CollapseIcon
                      : svgIcons.ExpandMoreIcon
                  }
                  className="size-7 grayscale"
                />
              </span>
            </h1>
            {subTitle && <div className="">{subTitle}</div>}
          </div>
        </button>

        {/* Report Type Selector */}
        {modalsState.showReportTypeSelector && (
          <>
            <div
              className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
              onClick={() => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showReportTypeSelector: false,
                  })
                );
              }}
            />
            <div
              className={`absolute top-32 mt-4 w-96 px-4 py-3 flex flex-col divide-y divide-gray-100 rounded-md border border-gray-100 bg-white shadow-lg z-modal`}
              role="menu"
            >
              <ReportTypeSelector
                showOnlyResultsOnSearch={true}
                singleColumn={true}
              />
            </div>
          </>
        )}

        {/* Sharing Buttons / Icons */}
        <div className="flex gap-2 ml-4">
          {reportOnDisplay?.is_shared_internally && (
            <button
              className="size-10 flex items-center justify-center rounded-lg hover:bg-accent-blue-paleO66 focus:bg-accent-blue-paleO66"
              onClick={handleInternalShareButtonClick}
            >
              <img src={svgIcons.UsersIcon} alt="Users" className="w-6" />
            </button>
          )}
          {reportOnDisplay?.is_shared_externally && (
            <button
              className="size-10 flex items-center justify-center rounded-lg hover:bg-accent-blue-paleO66 focus:bg-accent-blue-paleO66"
              onClick={handleShareButtonClick}
            >
              <img src={svgIcons.SharedExt} alt="Users" className="w-4" />
            </button>
          )}
          {reportOnDisplay?.is_externally_scheduled && (
            <button
              className="size-10 flex items-center justify-center rounded-lg hover:bg-accent-blue-paleO66 focus:bg-accent-blue-paleO66"
              onClick={handleShareButtonClick}
            >
              <img src={svgIcons.TimerIcon} alt="Timer" className="w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Right Component */}
      {rightComponent && rightComponent}

      {/* Refresh Button */}
      {showRefreshButton && (
        <div className="flex items-center">
          <button className="flex justify-between py-2">
            <p className="text-accent-blue-dark font-medium text-base leading-5">
              {t("refresh_data")}
            </p>
            <img
              src={svgIcons.RefreshIcon}
              alt="refresh-icon"
              className="pl-2 "
            />
          </button>
        </div>
      )}
    </header>
  );
};

export default HeaderBar;
